import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  addDoc,
  updateDoc,
  Timestamp,
} from 'firebase/firestore';
import { db } from '@/config/firebase';
import {
  PayrollRecord,
  PayrollStatus,
  PayPeriod,
  Benefit,
  BenefitType,
  Deduction,
  DeductionType,
  DeductionCategory,
  DeductionCalculationMethod,
  DeductionFrequency,
  DeductionStatus,
  DeductionLimit,
  DeductionTier,
  GarnishmentDetails,
  DeductionTemplate,
  TaxForm,
} from '@/models/payroll.model';

const PAYROLL_COLLECTION = 'payrollRecords';
const BENEFITS_COLLECTION = 'employeeBenefits';
const DEDUCTIONS_COLLECTION = 'employeeDeductions';
const TAX_FORMS_COLLECTION = 'taxForms';

export class PayrollService {
  // ========== Payroll Records ==========

  static async getAllPayrollRecords(): Promise<PayrollRecord[]> {
    const q = query(
      collection(db, PAYROLL_COLLECTION),
      orderBy('payDate', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => this.docToPayrollRecord(doc.id, doc.data()));
  }

  static async getPayrollRecordById(id: string): Promise<PayrollRecord | null> {
    const docRef = doc(db, PAYROLL_COLLECTION, id);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) {
      return null;
    }
    return this.docToPayrollRecord(docSnap.id, docSnap.data());
  }

  static async getPayrollRecordsByEmployee(employeeId: string): Promise<PayrollRecord[]> {
    const q = query(
      collection(db, PAYROLL_COLLECTION),
      where('employeeId', '==', employeeId),
      orderBy('payDate', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => this.docToPayrollRecord(doc.id, doc.data()));
  }

  static async getPayrollRecordsByPeriod(
    periodStart: Date,
    periodEnd: Date
  ): Promise<PayrollRecord[]> {
    const q = query(
      collection(db, PAYROLL_COLLECTION),
      where('periodStart', '>=', Timestamp.fromDate(periodStart)),
      where('periodStart', '<=', Timestamp.fromDate(periodEnd)),
      orderBy('periodStart', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => this.docToPayrollRecord(doc.id, doc.data()));
  }

  static async createPayrollRecord(recordData: Partial<PayrollRecord>): Promise<string> {
    const now = Timestamp.now();
    const data: any = {
      employeeId: recordData.employeeId || '',
      employeeName: recordData.employeeName || '',
      payPeriod: recordData.payPeriod || PayPeriod.biWeekly,
      periodStart: recordData.periodStart ? Timestamp.fromDate(recordData.periodStart) : now,
      periodEnd: recordData.periodEnd ? Timestamp.fromDate(recordData.periodEnd) : now,
      payDate: recordData.payDate ? Timestamp.fromDate(recordData.payDate) : now,
      status: recordData.status || PayrollStatus.draft,
      grossPay: recordData.grossPay || 0,
      regularHours: recordData.regularHours || null,
      overtimeHours: recordData.overtimeHours || null,
      hourlyRate: recordData.hourlyRate || null,
      salaryAmount: recordData.salaryAmount || null,
      bonuses: recordData.bonuses || 0,
      commissions: recordData.commissions || 0,
      otherEarnings: recordData.otherEarnings || 0,
      federalTax: recordData.federalTax || 0,
      stateTax: recordData.stateTax || 0,
      localTax: recordData.localTax || 0,
      socialSecurity: recordData.socialSecurity || 0,
      medicare: recordData.medicare || 0,
      otherDeductions: recordData.otherDeductions || 0,
      totalDeductions: recordData.totalDeductions || 0,
      netPay: recordData.netPay || 0,
      benefitsCost: recordData.benefitsCost || 0,
      notes: recordData.notes || null,
      processedBy: recordData.processedBy || null,
      processedAt: recordData.processedAt ? Timestamp.fromDate(recordData.processedAt) : null,
      createdAt: now,
      lastUpdated: now,
    };

    const docRef = await addDoc(collection(db, PAYROLL_COLLECTION), data);
    return docRef.id;
  }

  static async calculateDeductionsForPayroll(
    employeeId: string,
    grossPay: number,
    payDate: Date,
    payPeriod: PayPeriod
  ): Promise<{
    preTaxTotal: number;
    postTaxTotal: number;
    garnishmentTotal: number;
    total: number;
    breakdown: any[];
  }> {
    // Get active deductions for this employee
    const deductions = await this.getActiveDeductionsForPayroll(employeeId, payDate);
    
    // Import deduction calculator dynamically to avoid circular dependencies
    const { DeductionCalculatorService } = await import('./deduction-calculator.service');
    
    // Calculate taxable income (gross pay minus pre-tax deductions)
    // For now, we'll do a simple calculation
    // In production, this would be more sophisticated
    const preTaxDeductions = deductions.filter(d => d.type === DeductionType.preTax);
    const postTaxDeductions = deductions.filter(d => d.type === DeductionType.postTax);
    const garnishments = deductions.filter(d => d.type === DeductionType.garnishment);
    
    // Calculate pre-tax deductions
    let taxableIncome = grossPay;
    const preTaxResults = [];
    for (const deduction of preTaxDeductions) {
      const result = DeductionCalculatorService.calculateDeduction({
        deduction,
        grossPay,
        taxableIncome,
        netPayBeforePostTax: grossPay,
        payPeriod,
      });
      if (result.applied) {
        taxableIncome -= result.calculatedAmount;
        preTaxResults.push(result);
      }
    }
    
    // Calculate taxes (simplified - in production, use proper tax calculator)
    const estimatedTaxes = taxableIncome * 0.25; // Simplified 25% tax rate
    const netPayBeforePostTax = grossPay - preTaxResults.reduce((sum, r) => sum + r.calculatedAmount, 0) - estimatedTaxes;
    
    // Calculate post-tax deductions
    const postTaxResults = [];
    let runningNetPay = netPayBeforePostTax;
    for (const deduction of postTaxDeductions) {
      const result = DeductionCalculatorService.calculateDeduction({
        deduction,
        grossPay,
        taxableIncome: taxableIncome,
        netPayBeforePostTax: runningNetPay,
        payPeriod,
      });
      if (result.applied) {
        runningNetPay -= result.calculatedAmount;
        postTaxResults.push(result);
      }
    }
    
    // Calculate garnishments
    const garnishmentResults = [];
    for (const garnishment of garnishments) {
      const result = DeductionCalculatorService.calculateDeduction({
        deduction: garnishment,
        grossPay,
        taxableIncome: taxableIncome,
        netPayBeforePostTax: runningNetPay,
        payPeriod,
      });
      if (result.applied) {
        runningNetPay -= result.calculatedAmount;
        garnishmentResults.push(result);
      }
    }
    
    const preTaxTotal = preTaxResults.reduce((sum, r) => sum + r.calculatedAmount, 0);
    const postTaxTotal = postTaxResults.reduce((sum, r) => sum + r.calculatedAmount, 0);
    const garnishmentTotal = garnishmentResults.reduce((sum, r) => sum + r.calculatedAmount, 0);
    
    return {
      preTaxTotal,
      postTaxTotal,
      garnishmentTotal,
      total: preTaxTotal + postTaxTotal + garnishmentTotal,
      breakdown: [...preTaxResults, ...postTaxResults, ...garnishmentResults],
    };
  }

  static async updatePayrollRecord(
    id: string,
    updates: Partial<PayrollRecord>
  ): Promise<void> {
    const docRef = doc(db, PAYROLL_COLLECTION, id);
    const updateData: any = {
      lastUpdated: Timestamp.now(),
    };

    if (updates.status !== undefined) updateData.status = updates.status;
    if (updates.grossPay !== undefined) updateData.grossPay = updates.grossPay;
    if (updates.totalDeductions !== undefined) updateData.totalDeductions = updates.totalDeductions;
    if (updates.netPay !== undefined) updateData.netPay = updates.netPay;
    if (updates.processedBy !== undefined) updateData.processedBy = updates.processedBy;
    if (updates.processedAt !== undefined) updateData.processedAt = updates.processedAt ? Timestamp.fromDate(updates.processedAt) : null;
    if (updates.notes !== undefined) updateData.notes = updates.notes;

    await updateDoc(docRef, updateData);
  }

  // ========== Benefits ==========

  static async getBenefitsByEmployee(employeeId: string): Promise<Benefit[]> {
    const q = query(
      collection(db, BENEFITS_COLLECTION),
      where('employeeId', '==', employeeId),
      orderBy('enrollmentDate', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => this.docToBenefit(doc.id, doc.data()));
  }

  static async getAllBenefits(): Promise<Benefit[]> {
    const q = query(
      collection(db, BENEFITS_COLLECTION),
      orderBy('enrollmentDate', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => this.docToBenefit(doc.id, doc.data()));
  }

  static async getBenefitById(id: string): Promise<Benefit | null> {
    const docRef = doc(db, BENEFITS_COLLECTION, id);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) {
      return null;
    }
    return this.docToBenefit(docSnap.id, docSnap.data());
  }

  static async createBenefit(benefitData: Partial<Benefit>): Promise<string> {
    const now = Timestamp.now();
    const data: any = {
      employeeId: benefitData.employeeId || '',
      employeeName: benefitData.employeeName || '',
      type: benefitData.type || BenefitType.health,
      provider: benefitData.provider || null,
      planName: benefitData.planName || null,
      coverageLevel: benefitData.coverageLevel || 'individual',
      employeeContribution: benefitData.employeeContribution || 0,
      employerContribution: benefitData.employerContribution || 0,
      totalCost: benefitData.totalCost || 0,
      effectiveDate: benefitData.effectiveDate ? Timestamp.fromDate(benefitData.effectiveDate) : now,
      endDate: benefitData.endDate ? Timestamp.fromDate(benefitData.endDate) : null,
      enrollmentDate: benefitData.enrollmentDate ? Timestamp.fromDate(benefitData.enrollmentDate) : now,
      status: benefitData.status || 'active',
      dependents: benefitData.dependents || [],
      createdAt: now,
      lastUpdated: now,
    };

    const docRef = await addDoc(collection(db, BENEFITS_COLLECTION), data);
    return docRef.id;
  }

  static async updateBenefit(
    id: string,
    updates: Partial<Benefit>
  ): Promise<void> {
    const docRef = doc(db, BENEFITS_COLLECTION, id);
    const updateData: any = {
      lastUpdated: Timestamp.now(),
    };

    if (updates.employeeId !== undefined) updateData.employeeId = updates.employeeId;
    if (updates.employeeName !== undefined) updateData.employeeName = updates.employeeName;
    if (updates.type !== undefined) updateData.type = updates.type;
    if (updates.provider !== undefined) updateData.provider = updates.provider;
    if (updates.planName !== undefined) updateData.planName = updates.planName;
    if (updates.coverageLevel !== undefined) updateData.coverageLevel = updates.coverageLevel;
    if (updates.employeeContribution !== undefined) updateData.employeeContribution = updates.employeeContribution;
    if (updates.employerContribution !== undefined) updateData.employerContribution = updates.employerContribution;
    if (updates.totalCost !== undefined) updateData.totalCost = updates.totalCost;
    if (updates.effectiveDate !== undefined) updateData.effectiveDate = updates.effectiveDate ? Timestamp.fromDate(updates.effectiveDate) : null;
    if (updates.endDate !== undefined) updateData.endDate = updates.endDate ? Timestamp.fromDate(updates.endDate) : null;
    if (updates.enrollmentDate !== undefined) updateData.enrollmentDate = updates.enrollmentDate ? Timestamp.fromDate(updates.enrollmentDate) : null;
    if (updates.status !== undefined) updateData.status = updates.status;
    if (updates.dependents !== undefined) updateData.dependents = updates.dependents;

    await updateDoc(docRef, updateData);
  }

  static async deleteBenefit(id: string): Promise<void> {
    const docRef = doc(db, BENEFITS_COLLECTION, id);
    await updateDoc(docRef, {
      status: 'terminated',
      lastUpdated: Timestamp.now(),
    });
  }

  // ========== Deductions ==========

  static async getAllDeductions(): Promise<Deduction[]> {
    const q = query(
      collection(db, DEDUCTIONS_COLLECTION),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => this.docToDeduction(doc.id, doc.data()));
  }

  static async getDeductionsByEmployee(employeeId: string, includeInactive = false): Promise<Deduction[]> {
    const constraints: any[] = [
      where('employeeId', '==', employeeId),
    ];
    
    if (!includeInactive) {
      constraints.push(where('status', '==', 'active'));
    }
    
    constraints.push(orderBy('priority', 'asc'));
    constraints.push(orderBy('startDate', 'desc'));
    
    const q = query(
      collection(db, DEDUCTIONS_COLLECTION),
      ...constraints
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => this.docToDeduction(doc.id, doc.data()));
  }

  static async getDeductionById(id: string): Promise<Deduction | null> {
    const docRef = doc(db, DEDUCTIONS_COLLECTION, id);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) {
      return null;
    }
    return this.docToDeduction(docSnap.id, docSnap.data());
  }

  static async getActiveDeductionsForPayroll(
    employeeId: string,
    payDate: Date
  ): Promise<Deduction[]> {
    const q = query(
      collection(db, DEDUCTIONS_COLLECTION),
      where('employeeId', '==', employeeId),
      where('status', '==', 'active')
    );
    const snapshot = await getDocs(q);
    
    return snapshot.docs
      .map(doc => this.docToDeduction(doc.id, doc.data()))
      .filter(d => {
        const startDate = d.startDate;
        const endDate = d.endDate;
        const terminationDate = d.terminationDate;
        
        if (startDate > payDate) return false;
        if (endDate && endDate < payDate) return false;
        if (terminationDate && terminationDate < payDate) return false;
        
        return true;
      });
  }

  static async createDeduction(deductionData: Partial<Deduction>, createdBy: string): Promise<string> {
    const now = Timestamp.now();
    const data: any = {
      employeeId: deductionData.employeeId || '',
      employeeName: deductionData.employeeName || '',
      employerId: deductionData.employerId || null,
      type: deductionData.type || DeductionType.postTax,
      category: deductionData.category || DeductionCategory.other,
      name: deductionData.name || '',
      description: deductionData.description || null,
      calculationMethod: deductionData.calculationMethod || DeductionCalculationMethod.fixedAmount,
      amount: deductionData.amount || null,
      percentage: deductionData.percentage || null,
      percentageBase: deductionData.percentageBase || null,
      tiers: deductionData.tiers || null,
      customCalculationFormula: deductionData.customCalculationFormula || null,
      limits: deductionData.limits || null,
      frequency: deductionData.frequency || DeductionFrequency.perPaycheck,
      startDate: deductionData.startDate ? Timestamp.fromDate(deductionData.startDate) : now,
      endDate: deductionData.endDate ? Timestamp.fromDate(deductionData.endDate) : null,
      effectiveDate: deductionData.effectiveDate ? Timestamp.fromDate(deductionData.effectiveDate) : null,
      terminationDate: deductionData.terminationDate ? Timestamp.fromDate(deductionData.terminationDate) : null,
      status: deductionData.status || DeductionStatus.active,
      requiresApproval: deductionData.requiresApproval || false,
      approvedBy: deductionData.approvedBy || null,
      approvedAt: deductionData.approvedAt ? Timestamp.fromDate(deductionData.approvedAt) : null,
      garnishmentDetails: deductionData.garnishmentDetails ? this.serializeGarnishmentDetails(deductionData.garnishmentDetails) : null,
      priority: deductionData.priority ?? 999,
      postTaxPriority: deductionData.postTaxPriority ?? null,
      isMandatory: deductionData.isMandatory || false,
      requiresEmployeeConsent: deductionData.requiresEmployeeConsent || false,
      employeeConsentDate: deductionData.employeeConsentDate ? Timestamp.fromDate(deductionData.employeeConsentDate) : null,
      employeeConsentSignature: deductionData.employeeConsentSignature || null,
      legalDocumentUrl: deductionData.legalDocumentUrl || null,
      complianceNotes: deductionData.complianceNotes || null,
      totalDeductedYTD: deductionData.totalDeductedYTD || 0,
      totalDeductedLifetime: deductionData.totalDeductedLifetime || 0,
      lastDeductionDate: null,
      lastDeductionAmount: null,
      deductionCount: 0,
      createdBy,
      createdAt: now,
      lastUpdated: now,
      lastUpdatedBy: createdBy,
      version: 1,
      vendorId: deductionData.vendorId || null,
      accountNumber: deductionData.accountNumber || null,
      notes: deductionData.notes || null,
      tags: deductionData.tags || [],
      metadata: deductionData.metadata || {},
    };

    const docRef = await addDoc(collection(db, DEDUCTIONS_COLLECTION), data);
    return docRef.id;
  }

  static async updateDeduction(
    id: string,
    updates: Partial<Deduction>,
    updatedBy: string
  ): Promise<void> {
    const currentDoc = await getDoc(doc(db, DEDUCTIONS_COLLECTION, id));
    
    if (!currentDoc.exists()) {
      throw new Error('Deduction not found');
    }
    
    const currentData = currentDoc.data();
    const updateData: any = {
      lastUpdated: Timestamp.now(),
      lastUpdatedBy: updatedBy,
      version: (currentData.version || 1) + 1,
    };

    // Update all provided fields
    if (updates.name !== undefined) updateData.name = updates.name;
    if (updates.description !== undefined) updateData.description = updates.description;
    if (updates.type !== undefined) updateData.type = updates.type;
    if (updates.category !== undefined) updateData.category = updates.category;
    if (updates.calculationMethod !== undefined) updateData.calculationMethod = updates.calculationMethod;
    if (updates.amount !== undefined) updateData.amount = updates.amount;
    if (updates.percentage !== undefined) updateData.percentage = updates.percentage;
    if (updates.percentageBase !== undefined) updateData.percentageBase = updates.percentageBase;
    if (updates.tiers !== undefined) updateData.tiers = updates.tiers;
    if (updates.customCalculationFormula !== undefined) updateData.customCalculationFormula = updates.customCalculationFormula;
    if (updates.limits !== undefined) updateData.limits = updates.limits;
    if (updates.frequency !== undefined) updateData.frequency = updates.frequency;
    if (updates.startDate !== undefined) updateData.startDate = Timestamp.fromDate(updates.startDate);
    if (updates.endDate !== undefined) updateData.endDate = updates.endDate ? Timestamp.fromDate(updates.endDate) : null;
    if (updates.effectiveDate !== undefined) updateData.effectiveDate = updates.effectiveDate ? Timestamp.fromDate(updates.effectiveDate) : null;
    if (updates.terminationDate !== undefined) updateData.terminationDate = updates.terminationDate ? Timestamp.fromDate(updates.terminationDate) : null;
    if (updates.status !== undefined) updateData.status = updates.status;
    if (updates.requiresApproval !== undefined) updateData.requiresApproval = updates.requiresApproval;
    if (updates.approvedBy !== undefined) updateData.approvedBy = updates.approvedBy;
    if (updates.approvedAt !== undefined) updateData.approvedAt = updates.approvedAt ? Timestamp.fromDate(updates.approvedAt) : null;
    if (updates.garnishmentDetails !== undefined) updateData.garnishmentDetails = updates.garnishmentDetails ? this.serializeGarnishmentDetails(updates.garnishmentDetails) : null;
    if (updates.priority !== undefined) updateData.priority = updates.priority;
    if (updates.postTaxPriority !== undefined) updateData.postTaxPriority = updates.postTaxPriority;
    if (updates.isMandatory !== undefined) updateData.isMandatory = updates.isMandatory;
    if (updates.requiresEmployeeConsent !== undefined) updateData.requiresEmployeeConsent = updates.requiresEmployeeConsent;
    if (updates.employeeConsentDate !== undefined) updateData.employeeConsentDate = updates.employeeConsentDate ? Timestamp.fromDate(updates.employeeConsentDate) : null;
    if (updates.employeeConsentSignature !== undefined) updateData.employeeConsentSignature = updates.employeeConsentSignature;
    if (updates.legalDocumentUrl !== undefined) updateData.legalDocumentUrl = updates.legalDocumentUrl;
    if (updates.complianceNotes !== undefined) updateData.complianceNotes = updates.complianceNotes;
    if (updates.totalDeductedYTD !== undefined) updateData.totalDeductedYTD = updates.totalDeductedYTD;
    if (updates.totalDeductedLifetime !== undefined) updateData.totalDeductedLifetime = updates.totalDeductedLifetime;
    if (updates.lastDeductionDate !== undefined) updateData.lastDeductionDate = updates.lastDeductionDate ? Timestamp.fromDate(updates.lastDeductionDate) : null;
    if (updates.lastDeductionAmount !== undefined) updateData.lastDeductionAmount = updates.lastDeductionAmount;
    if (updates.deductionCount !== undefined) updateData.deductionCount = updates.deductionCount;
    if (updates.vendorId !== undefined) updateData.vendorId = updates.vendorId;
    if (updates.accountNumber !== undefined) updateData.accountNumber = updates.accountNumber;
    if (updates.notes !== undefined) updateData.notes = updates.notes;
    if (updates.tags !== undefined) updateData.tags = updates.tags;
    if (updates.metadata !== undefined) updateData.metadata = updates.metadata;

    await updateDoc(doc(db, DEDUCTIONS_COLLECTION, id), updateData);
  }

  static async deleteDeduction(id: string, deletedBy: string): Promise<void> {
    // Soft delete by setting status to terminated
    await this.updateDeduction(id, { status: DeductionStatus.terminated }, deletedBy);
  }

  static async updateDeductionTracking(
    id: string,
    amount: number,
    deductionDate: Date
  ): Promise<void> {
    const docRef = doc(db, DEDUCTIONS_COLLECTION, id);
    const currentDoc = await getDoc(docRef);
    
    if (!currentDoc.exists()) {
      throw new Error('Deduction not found');
    }
    
    const currentData = currentDoc.data();
    const currentYTD = currentData.totalDeductedYTD || 0;
    const currentLifetime = currentData.totalDeductedLifetime || 0;
    const currentCount = currentData.deductionCount || 0;
    
    await updateDoc(docRef, {
      totalDeductedYTD: currentYTD + amount,
      totalDeductedLifetime: currentLifetime + amount,
      lastDeductionDate: Timestamp.fromDate(deductionDate),
      lastDeductionAmount: amount,
      deductionCount: currentCount + 1,
      lastUpdated: Timestamp.now(),
    });
  }

  // ========== Deduction Templates ==========

  static async getDeductionTemplates(): Promise<DeductionTemplate[]> {
    const q = query(
      collection(db, 'deductionTemplates'),
      orderBy('name', 'asc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => this.docToDeductionTemplate(doc.id, doc.data()));
  }

  static async createDeductionFromTemplate(
    templateId: string,
    employeeId: string,
    employeeName: string,
    createdBy: string
  ): Promise<string> {
    const templateDoc = await getDoc(doc(db, 'deductionTemplates', templateId));
    if (!templateDoc.exists()) {
      throw new Error('Template not found');
    }
    
    const template = templateDoc.data();
    const deductionData: Partial<Deduction> = {
      employeeId,
      employeeName,
      type: template.type,
      category: template.category,
      name: template.name,
      description: template.description,
      calculationMethod: template.calculationMethod,
      amount: template.defaultAmount,
      percentage: template.defaultPercentage,
      limits: template.defaultLimits,
    };
    
    return await this.createDeduction(deductionData, createdBy);
  }

  // ========== Tax Forms ==========

  static async getTaxFormsByEmployee(employeeId: string): Promise<TaxForm[]> {
    const q = query(
      collection(db, TAX_FORMS_COLLECTION),
      where('employeeId', '==', employeeId),
      orderBy('taxYear', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => this.docToTaxForm(doc.id, doc.data()));
  }

  static async createTaxForm(formData: Partial<TaxForm>): Promise<string> {
    const now = Timestamp.now();
    const data: any = {
      employeeId: formData.employeeId || '',
      employeeName: formData.employeeName || '',
      formType: formData.formType || 'w2',
      taxYear: formData.taxYear || new Date().getFullYear(),
      fileUrl: formData.fileUrl || '',
      generatedAt: now,
      generatedBy: formData.generatedBy || '',
      metadata: formData.metadata || {},
    };

    const docRef = await addDoc(collection(db, TAX_FORMS_COLLECTION), data);
    return docRef.id;
  }

  // ========== Helper Methods ==========

  private static docToPayrollRecord(id: string, data: any): PayrollRecord {
    return {
      id,
      employeeId: data.employeeId || '',
      employeeName: data.employeeName || '',
      payPeriod: data.payPeriod || PayPeriod.biWeekly,
      periodStart: data.periodStart?.toDate() || new Date(),
      periodEnd: data.periodEnd?.toDate() || new Date(),
      payDate: data.payDate?.toDate() || new Date(),
      status: data.status || PayrollStatus.draft,
      grossPay: data.grossPay || 0,
      regularHours: data.regularHours,
      overtimeHours: data.overtimeHours,
      hourlyRate: data.hourlyRate,
      salaryAmount: data.salaryAmount,
      bonuses: data.bonuses || 0,
      commissions: data.commissions || 0,
      otherEarnings: data.otherEarnings || 0,
      federalTax: data.federalTax || 0,
      stateTax: data.stateTax || 0,
      localTax: data.localTax || 0,
      socialSecurity: data.socialSecurity || 0,
      medicare: data.medicare || 0,
      otherDeductions: data.otherDeductions || 0,
      totalDeductions: data.totalDeductions || 0,
      netPay: data.netPay || 0,
      benefitsCost: data.benefitsCost || 0,
      notes: data.notes,
      processedBy: data.processedBy,
      processedAt: data.processedAt?.toDate(),
      createdAt: data.createdAt?.toDate() || new Date(),
      lastUpdated: data.lastUpdated?.toDate() || new Date(),
    };
  }

  private static docToBenefit(id: string, data: any): Benefit {
    return {
      id,
      employeeId: data.employeeId || '',
      employeeName: data.employeeName || '',
      type: data.type || BenefitType.health,
      provider: data.provider,
      planName: data.planName,
      coverageLevel: data.coverageLevel || 'individual',
      employeeContribution: data.employeeContribution || 0,
      employerContribution: data.employerContribution || 0,
      totalCost: data.totalCost || 0,
      effectiveDate: data.effectiveDate?.toDate() || new Date(),
      endDate: data.endDate?.toDate(),
      enrollmentDate: data.enrollmentDate?.toDate() || new Date(),
      status: data.status || 'active',
      dependents: data.dependents || [],
      createdAt: data.createdAt?.toDate() || new Date(),
      lastUpdated: data.lastUpdated?.toDate() || new Date(),
    };
  }

  private static docToDeduction(id: string, data: any): Deduction {
    return {
      id,
      employeeId: data.employeeId || '',
      employeeName: data.employeeName || '',
      employerId: data.employerId,
      type: data.type || DeductionType.postTax,
      category: data.category || DeductionCategory.other,
      name: data.name || '',
      description: data.description,
      calculationMethod: data.calculationMethod || DeductionCalculationMethod.fixedAmount,
      amount: data.amount,
      percentage: data.percentage,
      percentageBase: data.percentageBase,
      tiers: data.tiers ? this.deserializeTiers(data.tiers) : undefined,
      customCalculationFormula: data.customCalculationFormula,
      limits: data.limits ? this.deserializeLimits(data.limits) : undefined,
      frequency: data.frequency || DeductionFrequency.perPaycheck,
      startDate: data.startDate?.toDate() || new Date(),
      endDate: data.endDate?.toDate(),
      effectiveDate: data.effectiveDate?.toDate(),
      terminationDate: data.terminationDate?.toDate(),
      status: data.status || DeductionStatus.active,
      requiresApproval: data.requiresApproval || false,
      approvedBy: data.approvedBy,
      approvedAt: data.approvedAt?.toDate(),
      garnishmentDetails: data.garnishmentDetails ? this.deserializeGarnishmentDetails(data.garnishmentDetails) : undefined,
      priority: data.priority ?? 999,
      postTaxPriority: data.postTaxPriority,
      isMandatory: data.isMandatory || false,
      requiresEmployeeConsent: data.requiresEmployeeConsent || false,
      employeeConsentDate: data.employeeConsentDate?.toDate(),
      employeeConsentSignature: data.employeeConsentSignature,
      legalDocumentUrl: data.legalDocumentUrl,
      complianceNotes: data.complianceNotes,
      totalDeductedYTD: data.totalDeductedYTD || 0,
      totalDeductedLifetime: data.totalDeductedLifetime || 0,
      lastDeductionDate: data.lastDeductionDate?.toDate(),
      lastDeductionAmount: data.lastDeductionAmount,
      deductionCount: data.deductionCount || 0,
      createdBy: data.createdBy || '',
      createdAt: data.createdAt?.toDate() || new Date(),
      lastUpdated: data.lastUpdated?.toDate() || new Date(),
      lastUpdatedBy: data.lastUpdatedBy || '',
      version: data.version || 1,
      vendorId: data.vendorId,
      accountNumber: data.accountNumber,
      notes: data.notes,
      tags: data.tags || [],
      metadata: data.metadata || {},
    };
  }

  private static docToDeductionTemplate(id: string, data: any): DeductionTemplate {
    return {
      id,
      name: data.name || '',
      description: data.description,
      category: data.category || DeductionCategory.other,
      type: data.type || DeductionType.postTax,
      calculationMethod: data.calculationMethod || DeductionCalculationMethod.fixedAmount,
      defaultAmount: data.defaultAmount,
      defaultPercentage: data.defaultPercentage,
      defaultLimits: data.defaultLimits ? this.deserializeLimits(data.defaultLimits) : undefined,
      isStandard: data.isStandard || false,
      applicableToRoles: data.applicableToRoles || [],
      metadata: data.metadata || {},
      createdAt: data.createdAt?.toDate() || new Date(),
      lastUpdated: data.lastUpdated?.toDate() || new Date(),
    };
  }

  private static serializeGarnishmentDetails(details: GarnishmentDetails): any {
    return {
      type: details.type,
      caseNumber: details.caseNumber,
      courtOrderNumber: details.courtOrderNumber,
      courtOrderDate: details.courtOrderDate ? Timestamp.fromDate(details.courtOrderDate) : null,
      courtOrderExpiryDate: details.courtOrderExpiryDate ? Timestamp.fromDate(details.courtOrderExpiryDate) : null,
      priority: details.priority,
      exemptAmount: details.exemptAmount,
      maximumPercentage: details.maximumPercentage,
      attorneyFees: details.attorneyFees,
      processingFee: details.processingFee,
      recipientName: details.recipientName,
      recipientAddress: details.recipientAddress,
      recipientAccountNumber: details.recipientAccountNumber,
      recipientRoutingNumber: details.recipientRoutingNumber,
      isActive: details.isActive,
    };
  }

  private static deserializeGarnishmentDetails(data: any): GarnishmentDetails {
    return {
      type: data.type,
      caseNumber: data.caseNumber,
      courtOrderNumber: data.courtOrderNumber,
      courtOrderDate: data.courtOrderDate?.toDate(),
      courtOrderExpiryDate: data.courtOrderExpiryDate?.toDate(),
      priority: data.priority || 999,
      exemptAmount: data.exemptAmount,
      maximumPercentage: data.maximumPercentage,
      attorneyFees: data.attorneyFees,
      processingFee: data.processingFee,
      recipientName: data.recipientName,
      recipientAddress: data.recipientAddress,
      recipientAccountNumber: data.recipientAccountNumber,
      recipientRoutingNumber: data.recipientRoutingNumber,
      isActive: data.isActive !== false,
    };
  }

  private static deserializeTiers(data: any): DeductionTier[] {
    if (!Array.isArray(data)) return [];
    return data.map((tier: any) => ({
      from: tier.from || 0,
      to: tier.to,
      amount: tier.amount,
      percentage: tier.percentage,
    }));
  }

  private static deserializeLimits(data: any): DeductionLimit {
    return {
      minimum: data.minimum,
      maximum: data.maximum,
      annualMaximum: data.annualMaximum,
      lifetimeMaximum: data.lifetimeMaximum,
      perPaycheckMaximum: data.perPaycheckMaximum,
    };
  }

  private static docToTaxForm(id: string, data: any): TaxForm {
    return {
      id,
      employeeId: data.employeeId || '',
      employeeName: data.employeeName || '',
      formType: data.formType || 'w2',
      taxYear: data.taxYear || new Date().getFullYear(),
      fileUrl: data.fileUrl || '',
      generatedAt: data.generatedAt?.toDate() || new Date(),
      generatedBy: data.generatedBy || '',
      metadata: data.metadata || {},
    };
  }
}

